import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const clean = value => String(value || '').trim();
const create = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

function applyMerchandising(root = document) {
  const rules = [...document.querySelectorAll('[data-c21-label-rules]')].flatMap(node => {
    try { return JSON.parse(node.textContent); } catch { return []; }
  });
  root.querySelectorAll('[data-c21-badge], [data-c21-features]').forEach(node => {
    const rule = rules.find(item => item.handle === node.dataset.productHandle);
    const isBadge = node.hasAttribute('data-c21-badge');
    // Inventory status always takes priority over promotional overrides.
    const soldOut = isBadge && node.dataset.soldOut === 'false';
    const values = rule?.[isBadge ? 'badges' : 'features']?.map(clean).filter(Boolean);
    if (values?.length && !soldOut && node.dataset.c21Rule !== JSON.stringify(values)) {
      node.replaceChildren(...values.slice(0, isBadge ? 6 : 3).map((text, index) => {
        const span = create('span', '', text);
        span.hidden = isBadge && index > 0;
        return span;
      }));
      node.dataset.c21Rule = JSON.stringify(values);
      node.hidden = false;
    }
    if (isBadge && !node.dataset.c21Rotation) node.dataset.c21Rotation = '0';
  });
}

applyMerchandising();
let badgeScan;
new MutationObserver(() => {
  clearTimeout(badgeScan);
  badgeScan = setTimeout(() => applyMerchandising(), 120);
}).observe(document.body, { childList: true, subtree: true });

// One message remains visible. Rotation pauses for hover/focus and reduced motion.
setInterval(() => {
  if (document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('[data-c21-badge]').forEach(node => {
    if (node.closest('product-card, product-component')?.matches(':hover, :focus-within')) return;
    const messages = [...node.children];
    if (messages.length < 2 || !node.getClientRects().length) return;
    const index = (Number(node.dataset.c21Rotation) + 1) % messages.length;
    node.dataset.c21Rotation = String(index);
    messages.forEach((message, i) => { message.hidden = i !== index; });
  });
}, 4000);

const catalogNode = document.getElementById('c21-pair-catalog');
const dialog = document.getElementById('c21-pair-dialog');
if (catalogNode && dialog) {
  const catalog = JSON.parse(catalogNode.textContent);
  const groups = catalog.groups.filter(group => group.products.length);
  const form = dialog.querySelector('[data-c21-pair-form]');
  const addButton = dialog.querySelector('[data-c21-pair-add]');
  const status = dialog.querySelector('[data-c21-pair-status]');
  let currentGroup;
  let currentProduct;
  let currentOptions = [];
  let currentVariant;
  let cartSnapshot;
  let adding = false;
  let previousFocus;
  let requestSequence = 0;
  let buyNowRequest;
  let opening = false;
  let closingBuyNow = false;
  const laterButton = dialog.querySelector('[data-c21-pair-later]');
  const skipButton = dialog.querySelector('[data-c21-pair-skip]');
  const messages = JSON.parse(dialog.querySelector('[data-c21-pair-messages]').textContent);

  function finishBuyNow(items) {
    const request = buyNowRequest;
    if (!request) return;
    buyNowRequest = undefined;
    closingBuyNow = true;
    dialog.close();
    request.resolve(items);
  }
  // This is called by the theme's GoKwik handler before sending any Buy Now items.
  window.c21PrepareBuyNow = ({ variantId, quantity }) => {
    const items = [{ id: String(variantId), quantity }];
    const group = groups.find(group => group.products.some(product => product.variants.some(variant => String(variant.id) === String(variantId))));
    if (!group || quantity % 2 === 0) return Promise.resolve(items);
    if (adding || dialog.open || opening || buyNowRequest) return Promise.resolve(null);
    const product = group.products.find(product => product.variants.some(variant => String(variant.id) === String(variantId)));
    return new Promise(resolve => {
      buyNowRequest = { resolve, items };
      openGroup(group, { product_id: product.id, variant_id: variantId });
    });
  };
  skipButton.addEventListener('click', () => finishBuyNow(buyNowRequest?.items));
  const cartUrl = `${Theme.routes.cart_url}.js`;
  const getCart = async () => {
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your cart could not be loaded. Please try again.');
    return response.json();
  };
  const countGroup = (cart, group) => cart.items.reduce((total, item) => {
    return total + (group.products.some(product => String(product.id) === String(item.product_id)) ? item.quantity : 0);
  }, 0);
  const itemGroup = item => groups.find(group => group.products.some(product => String(product.id) === String(item.product_id)));

  function renderVariant() {
    currentVariant = currentProduct.variants.find(variant => variant.options.every((value, index) => value === currentOptions[index]));
    const complete = currentOptions.length === currentProduct.options.length && currentOptions.every(Boolean);
    const ready = complete && currentVariant?.available;
    addButton.disabled = adding || !ready;
    addButton.textContent = adding ? 'Adding…' : buyNowRequest ? messages.continue : 'Add second pair';
    status.textContent = !complete ? 'Choose your size and length.' : !ready ? 'This combination is unavailable. Choose another size, length or colour.' : `${currentVariant.price} before offer. Your discount is confirmed in the cart / checkout.`;
    dialog.querySelectorAll('[data-c21-option]').forEach(select => {
      const index = Number(select.dataset.c21Option);
      [...select.options].forEach(option => {
        if (!option.value) return;
        // Values remain selectable so an unavailable fit is explicit, never silently replaced.
        const available = currentProduct.variants.some(variant => variant.available && variant.options[index] === option.value && variant.options.every((value, i) => i === index || !currentOptions[i] || currentOptions[i] === value));
        option.textContent = `${option.value}${available ? '' : ' — unavailable'}`;
      });
    });
  }

  function renderProduct(product, preserve = true) {
    const optionKey = name => /length/i.test(name) ? 'length' : /size/i.test(name) ? 'size' : name.toLowerCase().trim();
    const names = (currentProduct?.options || []).map(optionKey);
    const values = [...currentOptions];
    currentProduct = product;
    currentOptions = product.options.map(name => preserve ? values[names.indexOf(optionKey(name))] || '' : '');
    const image = dialog.querySelector('[data-c21-pair-image]');
    image.src = product.image;
    image.alt = product.title;
    dialog.querySelector('[data-c21-pair-title]').textContent = product.title;
    dialog.querySelector('[data-c21-pair-colour-name]').textContent = product.colour;
    dialog.querySelectorAll('[data-c21-pair-product]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.c21PairProduct === product.handle)));
    const controls = dialog.querySelector('[data-c21-pair-options]');
    controls.replaceChildren();
    product.options.forEach((name, index) => {
      const label = create('label', 'c21-pair-option');
      label.append(create('span', '', /length/i.test(name) ? 'Length' : name));
      const select = create('select');
      select.dataset.c21Option = String(index);
      const placeholder = create('option', '', `Choose ${/length/i.test(name) ? 'length' : name.toLowerCase()}`);
      placeholder.value = '';
      select.append(placeholder);
      const values = [...new Set(product.variants.map(variant => variant.options[index]))];
      values.forEach(value => {
        const option = create('option', '', value);
        option.value = value;
        select.append(option);
      });
      if (!values.includes(currentOptions[index])) currentOptions[index] = '';
      select.value = currentOptions[index];
      select.addEventListener('change', () => {
        currentOptions[index] = select.value;
        renderVariant();
      });
      label.append(select);
      controls.append(label);
    });
    renderVariant();
  }

  function openGroup(group, firstItem) {
    if (adding || dialog.open || opening) return;
    closingBuyNow = false;
    laterButton.hidden = Boolean(buyNowRequest);
    skipButton.hidden = !buyNowRequest;
    currentGroup = group;
    previousFocus = document.activeElement;
    currentProduct = undefined;
    currentOptions = [];
    const firstProduct = group.products.find(product => String(product.id) === String(firstItem?.product_id)) || group.products[0];
    if (firstItem) {
      currentProduct = firstProduct;
      currentOptions = firstProduct.variants.find(variant => String(variant.id) === String(firstItem.variant_id))?.options || [];
    }
    const colours = dialog.querySelector('[data-c21-pair-colours]');
    colours.replaceChildren(...group.products.map(product => {
      const button = create('button', 'c21-pair-swatch');
      button.type = 'button';
      button.dataset.c21PairProduct = product.handle;
      button.setAttribute('aria-label', product.colour);
      button.title = product.colour;
      const dot = create('span');
      dot.style.setProperty('--shade', product.shade);
      button.append(dot, create('span', '', product.colour));
      button.addEventListener('click', () => renderProduct(product));
      return button;
    }));
    dialog.querySelector('[data-c21-pair-intro]').textContent = `Complete your ${group.title} offer pair. Pick another colour, size and length from this category.`;
    if (buyNowRequest) {
      dialog.querySelector('[data-c21-pair-intro]').textContent = messages.beforeCheckout.replace('[category]', group.title);
      dialog.querySelector('[data-c21-pair-first]').textContent = `${messages.firstPair}: ${firstProduct.title} · ${currentOptions.join(' / ')}`;
    }
    dialog.querySelector('[data-c21-pair-first]').hidden = !buyNowRequest;
    renderProduct(firstProduct, Boolean(firstItem));
    // Close native overlays through their controls so their focus and scroll state stay intact.
    document.querySelector('theme-drawer#cart-drawer[open]')?.close();
    document.querySelector('quick-add-dialog dialog[open]')?.querySelector('button[aria-label*="Close" i]')?.click();
    opening = true;
    setTimeout(() => {
      opening = false;
      if (!dialog.isConnected || dialog.open) return;
      try { dialog.showModal(); } catch {
        buyNowRequest?.resolve(null);
        buyNowRequest = undefined;
      }
    }, 250);
  }

  function showReminders(cart) {
    document.querySelectorAll('[data-c21-cart-pairs]').forEach(node => node.remove());
    const incomplete = groups.filter(group => countGroup(cart, group) % 2 === 1);
    document.querySelectorAll('.cart-drawer__items, .cart-items__wrapper:not(.cart-items__wrapper--drawer-empty)').forEach(container => {
      if (container.closest('.cart-drawer__items') && !container.matches('.cart-drawer__items')) return;
      // Only one panel per cart surface.
      if (container.closest('[data-c21-cart-pairs]') || container.parentElement?.querySelector(':scope > [data-c21-cart-pairs]')) return;
      if (!incomplete.length) return;
      const reminder = create('div', 'c21-cart-pairs');
      reminder.dataset.c21CartPairs = '';
      reminder.append(create('p', '', 'BUY 1 GET 1 · Complete your pair'));
      reminder.append(create('small', '', 'Pairs must be from the same category. A cotton + metallic combination does not qualify as a pair.'));
      incomplete.forEach(group => {
        const button = create('button', 'c21-cart-pair-link', `Select your second ${group.key} pair →`);
        button.type = 'button';
        button.addEventListener('click', () => openGroup(group, cart.items.find(item => itemGroup(item) === group)));
        reminder.append(button);
      });
      container.after(reminder);
    });
  }

  async function syncCart(autoOpen = false, productId) {
    const sequence = ++requestSequence;
    try {
      const cart = await getCart();
      if (sequence !== requestSequence) return;
      cartSnapshot = cart;
      showReminders(cart);
      const group = groups.find(group => group.products.some(product => String(product.id) === String(productId)));
      if (autoOpen && catalog.autoOpen && group && countGroup(cart, group) % 2 === 1) {
        openGroup(group, cart.items.find(item => String(item.product_id) === String(productId)));
      }
    } catch (error) { if (dialog.open) status.textContent = error.message; }
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    event.promise?.then(({ detail }) => {
      if (detail?.didError) return;
      setTimeout(() => syncCart(event.action === 'add' && !adding, detail?.productId), 150);
    }).catch(() => {});
  });
  document.addEventListener('theme-drawer:open', () => syncCart());
  document.addEventListener('cart-section:restored', () => syncCart());
  document.addEventListener('shopify:section:load', () => { applyMerchandising(); syncCart(); });
  syncCart();

  dialog.querySelectorAll('[data-c21-pair-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
  dialog.addEventListener('close', () => {
    const wasBuyNow = closingBuyNow || Boolean(buyNowRequest);
    if (buyNowRequest) { buyNowRequest.resolve(null); buyNowRequest = undefined; }
    closingBuyNow = false;
    previousFocus?.focus?.();
    if (!wasBuyNow && cartSnapshot?.items.length) document.querySelector('theme-drawer#cart-drawer')?.open();
  });
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (adding || !currentVariant?.available) return;
    if (buyNowRequest) {
      const items = [...buyNowRequest.items, { id: String(currentVariant.id), quantity: 1 }];
      finishBuyNow(items);
      return;
    }
    adding = true;
    renderVariant();
    const variant = currentVariant;
    const deferred = CartLinesUpdateEvent.createPromise();
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId))];
    // Native event promise refreshes Horizon cart totals, items, discounts and header count.
    form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variant.id), quantity: 1 }], promise: deferred.promise }));
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: sectionIds, sections_url: location.pathname }) });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'This pair could not be added. Please select an available combination.');
      const cart = await getCart();
      cartSnapshot = cart;
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'c21-pair-picker', sourceId: 'c21-pair-dialog', itemCount: 1, productId: currentProduct.id, sections: data.sections, didError: false } });
      dialog.close();
      setTimeout(() => showReminders(cart), 250);
    } catch (error) {
      deferred.resolve({ cart: null, detail: { didError: true } });
      status.textContent = error.message;
    } finally {
      adding = false;
      addButton.disabled = !currentVariant?.available;
      addButton.textContent = 'Add second pair';
    }
  });
}
